import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { ERouteLinks } from 'models/route';
import { Table } from 'components/molecules/Table';
import { useTranslations } from 'hooks/useTranslations';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useNavigate } from 'react-router-dom';
import { CardList } from 'components/molecules/CardList';
import { EButtonVariants } from 'constants/Buttons';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { EIconTypes } from 'constants/Icons';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EPerPages, ISortOptions } from 'models/table';
import { Pagination } from 'components/molecules/Pagination';
import { useLang } from 'models/langContext';
import {
  AccessoriesAttachmentsTableColumns,
  mapAccessoriesAttachmentsToDataSource,
} from '../helpers';
import { AccessoriesAttachmentsCard } from '../AccessoriesAttachmentsCard';

export enum EAccessoryAttachmentsLabel {
  dateAndTime = 'accessoryDetails.attachments.DateAndTime.column',
  attachmentName = 'accessoryDetails.attachments.attachmentName.column',
  attachmentSize = 'accessoryDetails.attachments.attachmentSize.column',
  transactionType = 'accessoryDetails.attachments.transactionType.column',
  addAttachment = 'accessoryDetails.attachments.addAttachment.button',
}

interface IAccessoriesAttachments {
  idAncillary: string | undefined;
  hideManagement: boolean;
}
const AccessoriesAttachments = ({ idAncillary, hideManagement }: IAccessoriesAttachments) => {
  const { isDesktop } = useThemeBreakpoint();
  const navigate = useNavigate();
  const { t } = useTranslations();
  const {
    getAccessoriesAttachmentsData,
    accessoriesAttachments,
    paginatorAccessoriesAttachments,
    transactionType,
    getTransactionType,
  } = useAncillaries();
  const { longDateFormat, midDateFormat } = useGlobalProperty();
  const [isLoading, setIsLoading] = useState<boolean | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const { selectedLanguage } = useLang();

  useEffect(() => {
    getTransactionType();
  }, [selectedLanguage]);

  useEffect(() => {
    (async function getDataInit() {
      setIsPaginationLoading(true);
      setIsLoading(true);
      if (idAncillary) {
        await getAccessoriesAttachmentsData(idAncillary, { currentPage, perPage, sort });
      }
      setIsLoading(false);
      setIsPaginationLoading(false);
    })();
  }, [idAncillary, currentPage, perPage, sort]);

  return (
    <div>
      {!hideManagement && (
        <Grid
          size={{ xs: 12 }}
          container
          sx={{ display: 'flex', justifyContent: 'flex-end', margin: '24px 0' }}
        >
          <Button
            variant={EButtonVariants.contained}
            icon={EIconTypes.plus}
            label={t(EAccessoryAttachmentsLabel.addAttachment)}
            onClick={() =>
              idAncillary
                ? navigate(ERouteLinks.AccessoriesAddAttachment.replace(':id', idAncillary))
                : {}
            }
          />
        </Grid>
      )}
      {isDesktop ? (
        <Table
          columns={AccessoriesAttachmentsTableColumns({
            ancillaryId: idAncillary ?? '',
            handleView: (id: string) =>
              window.open(
                ERouteLinks.AccessoriesViewAttachment.replace(':id', id).replace(
                  ':ancillaryId',
                  idAncillary ?? '',
                ),
                '_blank',
              ),
            handleDelete: (id: string) =>
              navigate(
                ERouteLinks.AccessoriesDeleteAttachment.replace(':id', id).replace(
                  ':ancillaryId',
                  idAncillary ?? '',
                ),
                { state: { accessoriesAttachments } },
              ),
            dateFormat: longDateFormat,
            hideManagement,
          })}
          dataSource={mapAccessoriesAttachmentsToDataSource(
            accessoriesAttachments,
            transactionType,
            midDateFormat,
          )}
          onSort={setSort}
          loading={isLoading === true}
        />
      ) : (
        <CardList
          items={mapAccessoriesAttachmentsToDataSource(
            accessoriesAttachments,
            transactionType,
            midDateFormat,
          )}
          render={(data, index) => (
            <AccessoriesAttachmentsCard
              data={data}
              key={index}
              idAncillary={idAncillary}
              handleView={(id: string) =>
                window.open(
                  ERouteLinks.AccessoriesViewAttachment.replace(':id', id).replace(
                    ':ancillaryId',
                    idAncillary ?? '',
                  ),
                  '_blank',
                )
              }
              handleDelete={(id: string) =>
                navigate(
                  ERouteLinks.AccessoriesDeleteAttachment.replace(':id', id).replace(
                    ':ancillaryId',
                    idAncillary ?? '',
                  ),
                  { state: { accessoriesAttachments } },
                )
              }
            />
          )}
        />
      )}
      {paginatorAccessoriesAttachments && (
        <Pagination
          count={paginatorAccessoriesAttachments?.totalElements}
          perPage={perPage}
          onChangePage={(page) => {
            setCurrentPage(page);
          }}
          onChangePerPage={(value) => {
            setPerPage(value);
          }}
          current={currentPage}
          isLoading={isPaginationLoading}
        />
      )}
    </div>
  );
};

export { AccessoriesAttachments };
